<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="600px" persistent>
    <v-card>
      <v-card-title>匯出預約資料</v-card-title>
      <v-card-text>
        <v-form ref="exportForm" v-model="exportFormValid">
          <v-container>
            <v-row>
              <v-col cols="12" sm="6">
                <v-menu
                  ref="startDateMenu"
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="exportFilters.startDate"
                      label="建立時間起於"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      clearable
                      :rules="[rules.required]"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="exportFilters.startDate"
                    @input="startDateMenu = false"
                    no-title
                    scrollable
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  ref="endDateMenu"
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="exportFilters.endDate"
                      label="建立時間結束於"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      clearable
                      :rules="[rules.required, rules.dateRange]"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="exportFilters.endDate"
                    @input="endDateMenu = false"
                    no-title
                    scrollable
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="exportFilters.statuses"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  label="處理狀態"
                  multiple
                  chips
                  deletable-chips
                  :rules="[rules.arrayRequired]"
                ></v-select>
              </v-col>
              <v-col cols="12" v-if="clinicUser">
                <v-input :value="exportFilters.doctors" :rules="[rules.arrayRequired]" style="display: none;"></v-input>
                <p class="font-weight-bold mb-0">醫師</p>
                <v-checkbox v-model="allDoctorsSelected" label="全選" @change="toggleAllDoctors"
                            class="d-inline-block mr-4"/>
                <v-checkbox
                  v-for="doctor in doctors"
                  :key="doctor.doctor_code"
                  v-model="exportFilters.doctors"
                  :label="doctor.doctor_name"
                  :value="doctor.doctor_code"
                  class="d-inline-block mr-4"
                />
              </v-col>
              <v-col cols="12" v-else>
                <v-input :value="exportFilters.clinics" :rules="[rules.arrayRequired]" style="display: none;"></v-input>
                <p class="font-weight-bold mb-0">診所</p>
                <v-checkbox v-model="allClinicsSelected" label="全選" @change="toggleAllClinics"
                            class="d-inline-block mr-4"/>
                <v-checkbox
                  v-for="clinic in clinics"
                  :key="clinic.clinic_code"
                  v-model="exportFilters.clinics"
                  :label="clinic.clinic_name"
                  :value="clinic.clinic_code"
                  class="d-inline-block mr-4"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog">取消</v-btn>
        <v-btn color="primary" @click="handleExport" :loading="exportLoading" :disabled="!exportFormValid">確認匯出</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import { getDoctorListAPI, getClinicListAPI, exportAppointmentsAPI } from "@/api";

export default {
  name: "ExportAppointmentDialog",
  props: {
    value: Boolean
  },
  data() {
    return {
      exportLoading: false,
      exportFormValid: false,
      startDateMenu: false,
      endDateMenu: false,
      exportFilters: {
        startDate: null,
        endDate: null,
        statuses: [],
        doctors: [],
        clinics: [],
      },
      statusOptions: [
        {text: '未處理', value: 0},
        {text: '此時段可預約', value: 1},
        {text: '已額外安排時間', value: 2},
        {text: '此時段無法預約', value: 3},
        {text: '病患自行取消', value: 4},
      ],
      doctors: [],
      allDoctorsSelected: false,
      clinics: [],
      allClinicsSelected: false,
      rules: {
        required: v => !!v || '此欄位為必填',
        arrayRequired: v => (Array.isArray(v) && v.length > 0) || '請至少選擇一項',
        dateRange: () => {
          const { startDate, endDate } = this.exportFilters;
          if (!startDate || !endDate) return true;
          const start = this.$moment(startDate);
          const end = this.$moment(endDate);
          if (end.isBefore(start)) {
            return '結束日期不能早於開始日期';
          }
          if (end.diff(start, 'months', true) > 1) {
            return '時間區間最多為一個月';
          }
          return true;
        }
      }
    }
  },
  computed: {
    ...mapGetters([
      'axiosParamData',
      'clinicUser',
    ]),
  },
  watch: {
    value(v) {
      if (v) {
        this.openDialog();
      }
    },
    'exportFilters.doctors'(newVal) {
      this.allDoctorsSelected = this.doctors.length > 0 && newVal.length === this.doctors.length;
    },
    'exportFilters.clinics'(newVal) {
      this.allClinicsSelected = this.clinics.length > 0 && newVal.length === this.clinics.length;
    },
  },
  methods: {
    openDialog() {
      this.exportFilters = { startDate: null, endDate: null, statuses: [], doctors: [], clinics: [] };
      if (this.$refs.exportForm) {
        this.$refs.exportForm.resetValidation();
      }
      if (this.clinicUser) {
        if (this.doctors.length === 0) {
          this.fetchDoctors();
        }
      } else {
        if (this.clinics.length === 0) {
          this.fetchClinics();
        }
      }
    },
    closeDialog() {
      this.$emit('input', false);
      this.$refs.exportForm.resetValidation();
    },
    async fetchDoctors() {
      try {
        const res = await getDoctorListAPI(this.axiosParamData);
        this.doctors = res.data;
      } catch (e) {
        console.error(e);
        alert('無法載入醫師列表');
      }
    },
    async fetchClinics() {
      try {
        const res = await getClinicListAPI(this.axiosParamData);
        this.clinics = res.data;
      } catch (e) {
        console.error(e);
        alert('無法載入診所列表');
      }
    },
    toggleAllDoctors() {
      if (this.allDoctorsSelected) {
        this.exportFilters.doctors = this.doctors.map(d => d.doctor_code);
      } else {
        this.exportFilters.doctors = [];
      }
    },
    toggleAllClinics() {
      if (this.allClinicsSelected) {
        this.exportFilters.clinics = this.clinics.map(c => c.clinic_code);
      } else {
        this.exportFilters.clinics = [];
      }
    },
    async handleExport() {
      this.$refs.exportForm.validate();
      await this.$nextTick();

      if (!this.exportFormValid) {
        return;
      }

      this.exportLoading = true;
      try {
        const params = {
          ...this.axiosParamData,
          start: this.exportFilters.startDate,
          end: this.exportFilters.endDate,
          filter_status: this.exportFilters.statuses,
        };

        if (this.clinicUser) {
          params.filter_doctor_code = this.exportFilters.doctors;
        } else {
          params.filter_clinic_code = this.exportFilters.clinics;
        }

        const response = await exportAppointmentsAPI(params);

        const blob = new Blob([response.data], {type: response.headers['content-type']});
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);

        const contentDisposition = response.headers['content-disposition'];
        let filename = '線上預約匯出.csv';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename=\"(.+?)\"/);
          if (filenameMatch && filenameMatch.length === 2)
            filename = decodeURI(filenameMatch[1]);
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

        this.closeDialog();
      } catch (e) {
        console.error(e);
        alert('匯出失敗');
      } finally {
        this.exportLoading = false;
      }
    },
  }
}
</script>
